/**
 * Minimal promise-based IndexedDB key-value store for offline data.
 * Values are stored as raw strings (encrypted or plain JSON), so payloads
 * are byte-for-byte compatible with the legacy localStorage storage.
 *
 * Falls back to localStorage when IndexedDB is unavailable (e.g. some
 * private-browsing modes), keeping the same string semantics.
 */

// Thrown when the browser refuses a write because storage is full.
// Callers must treat this as a failed persistence attempt, not a warning.
export class StorageQuotaError extends Error {
  constructor(message = 'Offline storage quota exceeded') {
    super(message);
    this.name = 'StorageQuotaError';
  }
}

const DB_NAME = 'junkyard-offline';
const DB_VERSION = 1;
const STORE_NAME = 'keyval';

export const isQuotaError = (error: unknown): boolean => {
  if (error instanceof StorageQuotaError) return true;
  if (typeof DOMException !== 'undefined' && error instanceof DOMException) {
    return (
      error.name === 'QuotaExceededError' ||
      error.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
      error.code === 22
    );
  }
  return false;
};

const isIndexedDBAvailable = (): boolean => {
  try {
    return typeof indexedDB !== 'undefined' && indexedDB !== null;
  } catch {
    return false;
  }
};

let dbPromise: Promise<IDBDatabase> | null = null;

const openDatabase = (): Promise<IDBDatabase> => {
  if (!dbPromise) {
    dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME);
        }
      };

      request.onsuccess = () => {
        const db = request.result;
        // Reopen lazily if the browser closes the connection (e.g. on
        // storage pressure or version change from another tab)
        db.onclose = () => {
          dbPromise = null;
        };
        db.onversionchange = () => {
          db.close();
          dbPromise = null;
        };
        resolve(db);
      };

      request.onerror = () => {
        dbPromise = null;
        reject(request.error ?? new Error('Failed to open offline database'));
      };
    });
  }
  return dbPromise;
};

const runReadTransaction = async (key: string): Promise<string | null> => {
  const db = await openDatabase();
  return new Promise<string | null>((resolve, reject) => {
    try {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const request = tx.objectStore(STORE_NAME).get(key);
      request.onsuccess = () => {
        resolve(typeof request.result === 'string' ? request.result : null);
      };
      request.onerror = () => {
        reject(request.error ?? new Error(`Failed to read key ${key}`));
      };
    } catch (error) {
      reject(error);
    }
  });
};

const runWriteTransaction = async (
  fn: (store: IDBObjectStore) => void
): Promise<void> => {
  const db = await openDatabase();
  return new Promise<void>((resolve, reject) => {
    try {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      fn(tx.objectStore(STORE_NAME));
      // Resolve on transaction completion (not request success) so the
      // write is actually durable before callers report success
      tx.oncomplete = () => resolve();
      tx.onabort = () => {
        reject(tx.error ?? new Error('Offline storage transaction aborted'));
      };
      tx.onerror = () => {
        reject(tx.error ?? new Error('Offline storage transaction failed'));
      };
    } catch (error) {
      reject(error);
    }
  });
};

export const kvGet = async (key: string): Promise<string | null> => {
  if (!isIndexedDBAvailable()) {
    return localStorage.getItem(key);
  }
  return runReadTransaction(key);
};

export const kvSet = async (key: string, value: string): Promise<void> => {
  try {
    if (!isIndexedDBAvailable()) {
      localStorage.setItem(key, value);
      return;
    }
    await runWriteTransaction(store => {
      store.put(value, key);
    });
  } catch (error) {
    if (isQuotaError(error)) {
      throw new StorageQuotaError();
    }
    throw error;
  }
};

export const kvRemove = async (key: string): Promise<void> => {
  if (!isIndexedDBAvailable()) {
    localStorage.removeItem(key);
    return;
  }
  await runWriteTransaction(store => {
    store.delete(key);
  });
};
